import {
  AppAcquisitionLinkClickEvent,
  AppAcquisitionLinkClickEventData,
} from "./AppAcquisitionLinkClickEvent";
import {
  AppBarImpressionEvent,
  AppBarImpressionEventData,
} from "./AppBarImpressionEvent";
import {
  AppBarRenderErrorEvent,
  AppBarRenderErrorEventData,
} from "./AppBarRenderErrorEvent";
import { AppIconErrorEvent, AppIconErrorEventData } from "./AppIconErrorEvent";
import { AppTileHoverEvent, AppTileHoverEventData } from "./AppTileHoverEvent";
import {
  ContentDroppedEvent,
  ContentDroppedEventData,
} from "./ContentDroppedEvent";
import {
  FlyoutButtonHoverEvent,
  FlyoutButtonHoverEventData,
} from "./FlyoutButtonHoverEvent";
import { FlyoutOpenedEvent, FlyoutOpenedEventData } from "./FlyoutOpenedEvent";
import { FlyoutRenderedEvent } from "./FlyoutRenderedEvent";
import { SeeAllButtonClickEvent } from "./SeeAllButtonClickEvent";
import {
  SuggestedLinkClickEvent,
  SuggestedLinkClickEventData,
} from "./SuggestedLinkClickEvent";
import {
  TileDescriptionEvent,
  TileDescriptionEventData,
} from "./TileDescriptionEvent";
import { TileInstallEvent, TileInstallEventData } from "./TileInstallEvent";
import { TileLaunchEvent, TileLaunchEventData } from "./TileLaunchEvent";
import { TileMoreInfoEvent, TileMoreInfoEventData } from "./TileMoreInfoEvent";
import {
  TilePermissionsEvent,
  TilePermissionsEventData,
} from "./TilePermissionsEvent";
import { TilePinEvent, TilePinEventData } from "./TilePinEvent";
import { TilePrivacyEvent, TilePrivacyEventData } from "./TilePrivacyEvent";
import { TileReorderEvent, TileReorderEventData } from "./TileReorderEvent";
import {
  TileTermsOfUseEvent,
  TileTermsOfUseEventData,
} from "./TileTermsOfUseEvent";
import {
  TileUninstallEvent,
  TileUninstallEventData,
} from "./TileUninstallEvent";
import { TileUnpinEvent, TileUnpinEventData } from "./TileUnpinEvent";
import { UserSearchEvent, UserSearchEventData } from "./UserSearchEvent";

export const AppBarEventOriginator = "AppBar";
export const FlyoutEventOriginator = "Flyout";
export const LaunchTileEventOriginator = "LaunchTile";
export const SuggestedTileEventOriginator = "SuggestedTile";
export const AppAcquisitionLinkEventOriginator = "AppAquisitionLink";
export const SuggestedLinkTileEventOriginator = "SuggestedLinkTile";
export const SeeAllButtonClickEventOriginator = "SeeAllButton";

export type AppBarEvent =
  | TileLaunchEvent
  | TileMoreInfoEvent
  | TilePrivacyEvent
  | TileTermsOfUseEvent
  | TilePermissionsEvent
  | TileDescriptionEvent
  | TileInstallEvent
  | TileUninstallEvent
  | AppAcquisitionLinkClickEvent
  | FlyoutButtonHoverEvent
  | FlyoutOpenedEvent
  | FlyoutRenderedEvent
  | AppTileHoverEvent
  | AppIconErrorEvent
  | AppBarRenderErrorEvent
  | AppBarImpressionEvent
  | UserSearchEvent
  | SuggestedLinkClickEvent
  | SeeAllButtonClickEvent
  | TilePinEvent
  | TileUnpinEvent
  | TileReorderEvent
  | ContentDroppedEvent;

export const AppBarEventTypes = {
  launch: "launch",
  moreInfo: "moreInfo",
  privacy: "privacy",
  termsOfUse: "termsOfUse",
  permissions: "permissions",
  description: "description",
  install: "install",
  uninstall: "uninstall",
  appAcquisitionLinkClick: "appAcquisitionLinkClick",
  flyoutButtonHover: "flyoutButtonHover",
  flyoutOpened: "flyoutOpened",
  flyoutRendered: "flyoutRendered",
  userSearch: "userSearch",
  appTileHover: "appTileHover",
  renderError: "renderError",
  appBarImpression: "appBarImpression",
  suggestedLinkClick: "suggestedLinkClick",
  seeAllButtonClick: "seeAllButtonClick",
  pin: "pin",
  unpin: "unpin",
  reorder: "reorder",
  appIconError: "appIconError",
  contentDropped: "contentDropped",
} as const;

export const AppBarEvents = {
  launch: (data?: () => TileLaunchEventData): TileLaunchEvent => ({
    originator: LaunchTileEventOriginator,
    type: AppBarEventTypes.launch,
    data,
  }),
  moreInfo: (data?: () => TileMoreInfoEventData): TileMoreInfoEvent => ({
    originator: LaunchTileEventOriginator,
    type: AppBarEventTypes.moreInfo,
    data,
  }),
  privacy: (data: () => TilePrivacyEventData): TilePrivacyEvent => ({
    originator: SuggestedTileEventOriginator,
    type: AppBarEventTypes.privacy,
    data,
  }),
  termsOfUse: (data: () => TileTermsOfUseEventData): TileTermsOfUseEvent => ({
    originator: SuggestedTileEventOriginator,
    type: AppBarEventTypes.termsOfUse,
    data,
  }),
  permissions: (
    data: () => TilePermissionsEventData
  ): TilePermissionsEvent => ({
    originator: SuggestedTileEventOriginator,
    type: AppBarEventTypes.permissions,
    data,
  }),
  description: (
    data: () => TileDescriptionEventData
  ): TileDescriptionEvent => ({
    originator: SuggestedTileEventOriginator,
    type: AppBarEventTypes.description,
    data,
  }),
  install: (data: () => TileInstallEventData): TileInstallEvent => ({
    originator: SuggestedTileEventOriginator,
    type: AppBarEventTypes.install,
    data,
  }),
  uninstall: (data?: () => TileUninstallEventData): TileUninstallEvent => ({
    originator: LaunchTileEventOriginator,
    type: AppBarEventTypes.uninstall,
    data,
  }),
  appAcquisitionLinkClick: (
    data?: () => AppAcquisitionLinkClickEventData
  ): AppAcquisitionLinkClickEvent => ({
    originator: AppAcquisitionLinkEventOriginator,
    type: AppBarEventTypes.appAcquisitionLinkClick,
    data,
  }),
  flyoutButtonHover: (
    data?: () => FlyoutButtonHoverEventData
  ): FlyoutButtonHoverEvent => ({
    originator: AppBarEventOriginator,
    type: AppBarEventTypes.flyoutButtonHover,
    data,
  }),
  flyoutOpened: (data?: () => FlyoutOpenedEventData): FlyoutOpenedEvent => ({
    originator: AppBarEventOriginator,
    type: AppBarEventTypes.flyoutOpened,
    data,
  }),
  flyoutRendered: (data?: () => void): FlyoutRenderedEvent => ({
    originator: AppBarEventOriginator,
    type: AppBarEventTypes.flyoutRendered,
    data,
  }),
  userSearch: (data?: () => UserSearchEventData): UserSearchEvent => ({
    originator: FlyoutEventOriginator,
    type: AppBarEventTypes.userSearch,
    data,
  }),
  pin: (data?: () => TilePinEventData): TilePinEvent => ({
    originator: LaunchTileEventOriginator,
    type: AppBarEventTypes.pin,
    data,
  }),
  unpin: (data?: () => TileUnpinEventData): TileUnpinEvent => ({
    originator: LaunchTileEventOriginator,
    type: AppBarEventTypes.unpin,
    data,
  }),
  reorder: (data?: () => TileReorderEventData): TileReorderEvent => ({
    originator: LaunchTileEventOriginator,
    type: AppBarEventTypes.reorder,
    data,
  }),
  appTileHover: (data?: () => AppTileHoverEventData): AppTileHoverEvent => ({
    originator: LaunchTileEventOriginator,
    type: AppBarEventTypes.appTileHover,
    data,
  }),
  appIconError: (data?: () => AppIconErrorEventData): AppIconErrorEvent => ({
    originator: LaunchTileEventOriginator,
    type: AppBarEventTypes.appIconError,
    data,
  }),
  renderError: (
    data?: () => AppBarRenderErrorEventData
  ): AppBarRenderErrorEvent => ({
    originator: AppBarEventOriginator,
    type: AppBarEventTypes.renderError,
    data,
  }),
  appBarImpression: (
    data?: () => AppBarImpressionEventData
  ): AppBarImpressionEvent => ({
    originator: AppBarEventOriginator,
    type: AppBarEventTypes.appBarImpression,
    data,
  }),
  suggestedLinkClick: (
    data: () => SuggestedLinkClickEventData
  ): SuggestedLinkClickEvent => ({
    originator: SuggestedLinkTileEventOriginator,
    type: AppBarEventTypes.suggestedLinkClick,
    data,
  }),
  seeAllButtonClick: (): SeeAllButtonClickEvent => ({
    originator: SeeAllButtonClickEventOriginator,
    type: AppBarEventTypes.seeAllButtonClick,
  }),
  contentDropped: (
    data: () => ContentDroppedEventData
  ): ContentDroppedEvent => ({
    originator: LaunchTileEventOriginator,
    type: AppBarEventTypes.contentDropped,
    data,
  }),
};
